<script setup lang="ts">
import { computed, defineEmits, defineProps, withDefaults } from 'vue';
import { getQuery, withQuery } from 'ufo';
import type { IApiMediaWithCrops } from "~/models/generated/IApiMediaWithCrops";

type Size = 'xs' | 'sm' | 'md' | 'lg';

interface Props {
  to?: string | null;
  size?: Size;
  name?: string | null;
  picture?: IApiMediaWithCrops[] | null;
  round?: boolean;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  to: null,
  size: 'md',
  name: null,
  picture: null,
  round: false,
  loading: false,
});

const emit = defineEmits(['click']);

const pixels = computed(() => {
  switch (props.size) {
    case 'xs':
      return 36;
    case 'sm':
      return 50;
    case 'lg':
      return 150;
    default:
      return 100;
  }
});

function cropUrl(width: number, height?: number): string { 
  if (!props.picture?.[0].url) {
    return '';
  }

  const url = props.picture[0].url;
  const baseUrl = url.split('?')[0];
  const existingQuery = getQuery(url);

  existingQuery.width = width.toString();
  existingQuery.height = (height ?? width).toString();

  if (!existingQuery.mode) {
    existingQuery.mode = 'crop';
  }

  return withQuery(baseUrl, existingQuery);
}

const pictureUrl = computed(() => {
  return cropUrl(pixels.value);
});

const pictureUrlRetina = computed(() => {
  return cropUrl(pixels.value * 2);
});

const classes = computed(() => {
  const result = [`display-picture--${props.size}`];

  if (props.round) {
    result.push('display-picture--round');
  }

  if (!props.picture) {
    result.push('display-picture--no-image');
  }

  return result;
});

const initials = computed(() => {
  if (props.name && props.name !== '(Anonymous)') {
    const split = props.name.split(' ');
    
    if ( split.length === 1 ) {
      return split[0].charAt(0).toUpperCase();
    }
    
    const first = split[0].charAt(0);
    const last = split[split.length - 1].charAt(0);
    return `${first}${last}`.toUpperCase();
  } else {
    return '??';
  }
});
</script>

<template>
  <component
    :is="to ? 'nuxt-link' : 'div'"
    :to="to"
    class="display-picture"
    :class="classes"
    @click="$emit('click')"
  >
<!--    <nuxt-img-->
<!--      v-if="picture"-->
<!--      :src="pictureUrl"-->
<!--      :srcset="`${pictureUrl} 1x, ${pictureUrlRetina} 2x`"-->
<!--      :width="pixels"-->
<!--      :height="pixels"-->
<!--      alt=""-->
<!--      class="display-picture__image"-->
<!--    />-->

    <span class="display-picture__no-image">
      <template v-if="loading">
        <loading-spinner block />
      </template>
      <template v-else>
        {{ initials }}
      </template>
    </span>
  </component>
</template>

<style lang="scss" scoped>
.display-picture {
  position: relative;
  flex: 75px 0 0;
  background: #eee;
  height: 75px;
  width: 75px;

  &--no-image {
    background: var(--primary) !important;
  }

  &__image,
  &__image :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__no-image {
    user-select: none;
    width: 100%;
    height: 100%;
    font-weight: 500;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 1.5rem;
  }

  &--round {
    border-radius: 100%;
    overflow: hidden;

    .display-picture__image,
    img {
      border-radius: 100%;
    }
  }

  &--xs {
    flex-basis: 36px;
    height: 36px;
    width: 36px;

    .display-picture__no-image {
      font-size: 1rem;
    }
  }

  &--sm {
    flex-basis: 50px;
    height: 50px;
    width: 50px;

    .display-picture__no-image {
      font-size: 1rem;
    }
  }

  &--lg {
    flex-basis: 150px;
    height: 150px;
    width: 150px;

    .display-picture__no-image {
      font-size: 3.5rem;
    }
  }
}

.dark-mode {
  .display-picture {
    background: #333;
  }
}
</style>
